import { NotificationCategory } from '~/server/common/enums';
import { createNotificationProcessor } from '~/server/notifications/base.notifications';

/**
 * App Listing COLLABORATORS — seat + ownership-transfer notifications.
 *
 * IMPERATIVE types (emitted directly from the services POST-COMMIT via
 * `createNotification`, behind the `notifyAppCollaborator` helper) — NOT scanned by a
 * scheduled `prepareQuery`, so they carry only a `prepareMessage`. Same shape as the
 * sibling `app-block.notifications` / `app-listing.notifications` families.
 *
 * 🔴 REUSING THIS PATH IS A DELIBERATE DIVERGENCE FROM THE PRIOR ART.
 * `EntityCollaborator` delivers its invite as a SYSTEM CHAT MESSAGE (`upsertChat` +
 * `createMessage` with `userId: -1`). That drags the whole chat subsystem into an
 * App Blocks flow, has no idempotency key, and is not toggleable or dismissible like a
 * notification. The App Blocks surface already has a notification path; using it keeps
 * the seat lifecycle on the same rails as approve/reject.
 *
 * The `type` strings are free-form (the notifications app stores them as text, no DB
 * enum), so adding these needs NO notifications-DB migration. Registered in
 * `utils.notifications.ts`.
 */

export type AppCollaboratorNotificationDetails = {
  /** The listing's public slug — identity + link target. */
  slug: string;
  /** The store listing the seat/transfer belongs to (the stable key). */
  appListingId?: string | null;
  /**
   * The backing AppBlock id, when there is one. 🔴 `null` for an OFF-SITE listing —
   * seats are listing-keyed and off-site listings have no AppBlock, so this is a
   * legitimately-absent value, not a missing one.
   */
  appBlockId?: string | null;
  /** Best-effort display name for nicer copy; absent ⇒ terser message. */
  name?: string | null;
};

/**
 * Where each notification points.
 *
 * 🔴 The per-app authoring pages are BLOCK-keyed (`/apps/[appBlockId]/…`), so an
 * off-site listing has no such page to deep-link to and falls back to `/apps`. That is
 * the pre-existing behaviour for a null `appBlockId`, kept deliberately rather than
 * inventing a route that does not exist.
 */
const APP_INVITES_URL = '/apps/invites';

function appUrl(details: AppCollaboratorNotificationDetails): string {
  return details.appBlockId ? `/apps/${details.appBlockId}` : '/apps';
}

/** `the app "Name"` when a name is present, else `the app <slug>`. */
function appLabel(details: AppCollaboratorNotificationDetails): string {
  const name = details.name?.trim();
  return name ? `the app "${name}"` : `the app ${details.slug}`;
}

export const appCollaboratorNotifications = createNotificationProcessor({
  'app-collaborator-invited': {
    displayName: 'App collaborator invitation',
    category: NotificationCategory.System,
    toggleable: false,
    prepareMessage: (notification) => {
      const details = notification.details as AppCollaboratorNotificationDetails;
      return {
        message: `You were invited to collaborate on ${appLabel(details)}.`,
        // 🔴 THE INVITE INBOX, not `appUrl`.
        //
        // A PENDING invitee owns nothing and holds no accepted seat, so no authoring
        // surface is reachable for them. `appUrl` would send them to `/apps/<appBlockId>`,
        // which for an approved app REDIRECTS to the public store detail
        // (`/apps/store-preview/<slug>`) — a page that renders perfectly well and says
        // NOTHING about the invitation, with no way to accept or decline it. For an
        // OFF-SITE listing there is no block id at all and the fallback is `/apps`, the
        // marketplace. Neither 404s; both are dead ends, which is the actual problem.
        //
        // (An earlier version of this comment claimed a 404. It was wrong — the block
        // route redirects rather than refusing — and a wrong mechanism sends the next
        // reader to the wrong file.)
        //
        // `/apps/invites` is keyed to the recipient's own user id
        // (`listMyPendingInvites`), which is the only key an invitee's own invitation has.
        url: APP_INVITES_URL,
      };
    },
  },
  'app-collaborator-accepted': {
    displayName: 'App collaborator accepted',
    category: NotificationCategory.System,
    toggleable: false,
    prepareMessage: (notification) => {
      const details = notification.details as AppCollaboratorNotificationDetails;
      return {
        message: `Your collaborator invitation for ${appLabel(details)} was accepted.`,
        url: appUrl(details),
      };
    },
  },
  'app-collaborator-removed': {
    displayName: 'App collaborator removed',
    category: NotificationCategory.System,
    toggleable: false,
    prepareMessage: (notification) => {
      const details = notification.details as AppCollaboratorNotificationDetails;
      return {
        message: `You are no longer a collaborator on ${appLabel(details)}.`,
        url: '/apps',
      };
    },
  },
  'app-ownership-transfer-offered': {
    displayName: 'App ownership transfer offered',
    category: NotificationCategory.System,
    toggleable: false,
    prepareMessage: (notification) => {
      const details = notification.details as AppCollaboratorNotificationDetails;
      return {
        message: `You were offered ownership of ${appLabel(details)}. Accept it to take over.`,
        // 🔴 THE INBOX, not the app page — the same target `app-collaborator-invited`
        // uses, and for the same reason. A pending offer confers NO role on the
        // recipient, so `/apps/<appBlockId>` resolves their access, finds none and
        // refuses; and an OFF-SITE listing has no such page at all, so `appUrl` fell
        // back to `/apps`, which has no accept control on it either. Pointing a
        // "accept it to take over" message at a page with no Accept button on it made
        // the notification unactionable in every case.
        url: APP_INVITES_URL,
      };
    },
  },
  'app-ownership-transfer-accepted': {
    displayName: 'App ownership transfer accepted',
    category: NotificationCategory.System,
    toggleable: false,
    prepareMessage: (notification) => {
      const details = notification.details as AppCollaboratorNotificationDetails;
      return {
        message: `Ownership of ${appLabel(details)} was transferred.`,
        url: appUrl(details),
      };
    },
  },
});
